import React, { useState } from 'react';
import { Box, Typography, IconButton, Paper } from "@mui/material";
import {
  LocationOn,
  CalendarToday,
  TrendingUp,
  Visibility,
  Edit,
  Delete,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { LocationModel } from "object-models/location-model";
import SnackBar from "components/SnackBar/SnackBar";
import AlertDialog from "components/AlertDialog";
import { ConfirmationMessages } from "constants/constant";
import { useLocationDelete } from "hooks/useLocationDelete";
import FormDialog from 'components/Dialog/modal-popup';
import CreateUpdateLocation from 'pages/CreateUpdateLocation';

interface LocationListViewProps {
  locations: LocationModel[];
  onLocationDeleted?: (locationId: number) => void;
  onLocationUpdated?: () => void;
}

const LocationListView: React.FC<LocationListViewProps> = ({
  locations,
  onLocationDeleted,
  onLocationUpdated
}) => {
  const navigate = useNavigate();
  const {
    openModal,
    success,
    setSuccess,
    selectedLocationName,
    handleDelete,
    openDialog,
    handleClose,
    handleConfirm,
  } = useLocationDelete(onLocationDeleted);

  const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');

  const getTotalEmissions = (location: LocationModel) => {
    const scope1 = location.scope1GHGEmission ?? 0;
    const scope2 = location.scope2GHGEmission ?? 0;
    const scope3 = location.scope3GHGEmission ?? 0;
    return (scope1 + scope2 + scope3).toLocaleString("en-US", {
      maximumFractionDigits: 0,
    });
  };

  const handleLocationClick = (locationId: number) => {
    localStorage.removeItem("subAreaName");
    localStorage.setItem("LocationId", String(locationId));
    localStorage.setItem("Portfolioview", "0");
    navigate(`/Location/${locationId}/Location-Details`);
  };

      const handleEdit = (locationId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        localStorage.setItem('LocationId', String(locationId));
        setDialogTitle('Edit Location Details');
        setDialogOpen(true);
    };

    const hideDialogHandler = () => {
        setDialogOpen(false);
    };

    const hideEditDialogHandler = () => {
        hideDialogHandler();
        onLocationUpdated?.();
    };

  return (
    <>
      <Box>
        {locations.map((location) => (
          <Paper
            key={location.locationId}
            onClick={() => handleLocationClick(location.locationId)}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: "pointer",
              "&:hover": {
                bgcolor: "action.hover",
                transform: "translateY(-2px)",
                transition: "all 0.2s",
              },
            }}
          >
            <LocationOn sx={{ color: "success.main", fontSize: 40 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: "1rem" }}
              >
                {location.locationName ?? "Location Name"}
              </Typography>
              <Box sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <TrendingUp sx={{ fontSize: 14 }} />
                  <strong>{getTotalEmissions(location)}</strong> tCO2e
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <CalendarToday sx={{ fontSize: 14 }} />
                  Baseline Year: {location.baseYear ?? "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Scope 1: {(location.scope1GHGEmission ?? 0).toLocaleString()}{" "}
                  tCO2e
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Scope 2: {(location.scope2GHGEmission ?? 0).toLocaleString()}{" "}
                  tCO2e
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Scope 3: {(location.scope3GHGEmission ?? 0).toLocaleString()}{" "}
                  tCO2e
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                sx={{ color: "primary.main" }}
              >
                <Visibility sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleEdit(location.locationId, e)}
                sx={{ color: "warning.main" }}
              >
                <Edit sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(location.locationId, e, location.locationName);
                }}
                sx={{ color: "error.main" }}
              >
                <Delete sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
        <FormDialog
                dialogOpen={dialogOpen}
                hideDialogHandler={hideDialogHandler}
                dialogTitle={dialogTitle}
                isFullScreenAllowed={true}
                maxWidth="lg"
                childComponent={
                    <CreateUpdateLocation isEdit={true} closeDialog={hideEditDialogHandler} />
                }
            />
      <AlertDialog
        open={openModal}
        openDialog={openDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        locationName={selectedLocationName}
        confirmationMessage={ConfirmationMessages.locationConfirmation}
      />
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          setSuccess({ show: false, severity: "info", message: "" })
        }
      />
    </>
  );
};

export default LocationListView;
